import { ComponentType } from "preact";
import { preact, JSX, $$ } from "../../Core/References";
import { $$requestManager } from "../../Core/RequestManager";
import { DataControl, IDataControlProps } from "../DataControl/DataControl";
import { DataPanel } from "../DataPanel/DataPanel";
import { Popup } from "../Popup/Popup";
import { RenderInBody } from "../RenderInBody/RenderInBody";
import { TextBox } from "../TextBox/TextBox";

export interface IAutoCompleteProps extends IDataControlProps
{
    AllowFreeText?: boolean;
    onChange?: Function;
    CreateComponent?: ComponentType;
    CreateData?: any;
    LookupService: string;
    Descriptor?: string;
    Context?: string;
    Watermark?: string;
}

export class AutoComplete extends DataControl<IAutoCompleteProps>
{
    private _activeItem: HTMLLIElement;
    private _addNewOption: HTMLLIElement;
    private _noMatchOption: HTMLLIElement;
    private _autoList: HTMLUListElement;
    private _context: string;
    private _currentText: string = "";
    private _editControl: DataPanel<unknown>;
    private _exclusions: any[] = [];
    private _currentAbortController: AbortController;
    private _imagePath: string;
    private _items: any[];
    private _showingOptions: boolean;
    private _timeoutBlur: number;
    private _timeoutSearch: number;
    private _name: string;
    private _value: string;
    private _popup: Popup;
    private _txtBox: TextBox;
    private _pnlMatches: HTMLElement;
    private _windowResizeHandler: Function;

    public constructor(props: IAutoCompleteProps)
    {
        super(props);
        this._windowResizeHandler = this._onWindowResize.bind(this);
    }

    private _onFocus()
    {
        this._txtBox.selectAll();
    }

    private _onBlur()
    {
        if (!this._timeoutBlur)
        {
            this._timeoutBlur = window.setTimeout(this._delayBlur.bind(this));
        }
    }

    private _delayBlur()
    {
        this._timeoutBlur = null;
        if (!this._txtBox.hasFocus())
        {
            this._closeSelection();
        }
    }

    public setExclusions(exclusions)
    {
        this._exclusions = exclusions || null;
    }

    public getText(): string
    {
        return this._txtBox.getValue();
    }

    public getContext(): string
    {
        return this._context;
    }

    public getImage(): string
    {
        return this._imagePath;
    }

    public focus()
    {
        this._txtBox && this._txtBox.focus();
    }

    private _doSearch()
    {
        if (!this._txtBox.hasFocus())
        {
            return;
        }

        var value = this._txtBox.getValue().trim();
        if (value === this._currentText)
        {
            return;
        }

        if (this._currentAbortController)
        {
            this._currentAbortController.abort();
            this._currentAbortController = null;
        }

        if (this._timeoutSearch)
        {
            clearTimeout(this._timeoutSearch);
            this._timeoutSearch = null;
        }

        this._currentText = value.replace('.', '.').trim();

        if (this._currentText.length > 1)
        {
            this._timeoutSearch = window.setTimeout(this._findMatches.bind(this), 200);
        }
        else
        {
            this._closeSelection();
        }
    }

    private _findMatches()
    {
        if (this._currentText && this._currentText.length > 0)
        {
            this._timeoutSearch = null;
            $$requestManager.skipShield();
            this._currentAbortController = $$requestManager.execute(this.props.LookupService, { Text: this._currentText, Context: this.props.Context, Exclusions: this._exclusions }, this._loadSuccess.bind(this), this._loadFailure.bind(this), true);
        }
    }

    private _loadSuccess(items: any[])
    {
        this._currentAbortController = null;

        this._autoList.innerHTML = "";

        this._items = items;

        if (this.props.AllowFreeText)
        {
            $$.appendChild(this._autoList, this._renderItem({ Key: "-1", Value: `Search for "${this._currentText}"` }, -1));
        }

        items.map((item, index) =>
        {
            $$.appendChild(this._autoList, this._renderItem(item, index));
        });

        if (this._addNewOption)
        {
            $$.removeCssClass(this._addNewOption, "selected");
            this._autoList.appendChild(this._addNewOption);
        }

        if (!this._addNewOption && items.length === 0)
        {
            this._autoList.appendChild(this._noMatchOption);
        }

        this._activeItem = this._autoList.firstChild as HTMLLIElement;
        if (!this._activeItem)
        {
            return;
        }

        if (this._activeItem !== this._noMatchOption)
        {
            this._activeItem.className = "selected";
        }

        $$.show(this._pnlMatches);
        window.addEventListener("resize", this._windowResizeHandler as any);
        this._showingOptions = true;
        this._position();
        this._pnlMatches.scrollTo(0, 0);
    }

    private _onWindowResize(event: UIEvent): void
    {
        this._position();
    }

    private _handleTxtKeyDown(textBox: TextBox, event: KeyboardEvent)
    {
        var key = event.key;

        if (this._showingOptions)
        {
            if (key === "ArrowUp" || key === "ArrowDown")
            {
                let newItem;
                if (key === "ArrowUp")
                {
                    newItem = this._activeItem.previousSibling;
                    event.preventDefault();
                }
                else
                {
                    newItem = this._activeItem.nextSibling;
                }

                if (newItem)
                {
                    $$.removeCssClass(this._activeItem, "selected");
                    $$.addCssClass(newItem, "selected");
                    newItem.scrollIntoView({ block: "nearest" });
                    this._activeItem = newItem;
                }
                return;
            }

            if (key === "Escape")
            {
                this._closeSelection();
                return;
            }

            if (key === "Tab")
            {
                this._finishSelection(true);
            }
        }
        else if (key === "Tab")
        {
            this._cleanUpValue();
        }

        if (key === "Backspace")
        {
            setTimeout(() => this._doSearch());
        }
    }

    private _cleanUpValue()
    {
        if (this._value && this._txtBox.getValue())
        {
            this.setValue(this._value, this._name, this._context, null, this._imagePath);
        }
        else
        {
            this.setValue(null, null, null, null, null);
        }
    }

    private _handleTxtKeyPress(textBox: TextBox, event: KeyboardEvent)
    {
        if (event.key === "Enter" && (this._showingOptions || this.props.AllowFreeText))
        {
            this._txtBox.selectAll();
            this._finishSelection();

            event.stopPropagation();
        }
        else
        {
            if (this._txtBox.getValue().trim() === "" && this._showingOptions)
            {
                this._closeSelection();
            }
            else
            {
                window.setTimeout(() => this._doSearch());
            }
        }
    }

    private _handleLstMouseDown(event)
    {
        let li = $(event.target).closest("li", this._pnlMatches).get(0) as HTMLLIElement;
        if (li)
        {
            this._activeItem = li;
            this._finishSelection();
        }
    }

    private _handleNew(item)
    {
        this._popup.close();
        var kvp = { Key: item.Id, Value: item.Name, Context: item.Context, Data: item, ImagePath: item.ImagePath };
        this.setValue(item.Id, item.Name, item.Context, item);

        if (this.props.onChange)
        {
            this.props.onChange(kvp);
        }
    }

    private _finishSelection(skipNew?)
    {
        if (this._activeItem === this._addNewOption)  //New option
        {
            if (!skipNew)
            {
                this._txtBox.setValue("");

                if (this.props.CreateComponent)
                {
                    this._editControl.clearData();
                    this._editControl.setData(this.props.CreateData);
                    this._popup.open()
                    this._editControl.focus();
                }
            }
            else
            {
                this._cleanUpValue();
            }
        }
        else
        {
            var item;
            if (!this._activeItem && this.props.AllowFreeText)
            {
                let text = this._txtBox.getValue().trim();
                if (text)
                {
                    item = { Key: null, Value: this._currentText };
                    this.setValue(item.Key, item.Value);
                }
            }
            else
            {
                let index = this._activeItem.dataset["index"];
                if (this.props.AllowFreeText && index == "-1")
                {
                    item = { Key: null, Value: this._currentText };
                    this.setValue(item.Key, item.Value);
                }
                else
                {
                    item = this._items[index];
                    this.setValue(item.Key, item.Value, item.Context, item.Data, item.ImagePath);
                }
            }

            if (this.props.onChange)
            {
                this.props.onChange(item);
            }
        }
        this._closeSelection();
    }

    public setValue(value, name?, context?, data?, imagePath?)
    {
        this._value = value;
        this._context = context;
        this._name = name;
        this._imagePath = imagePath;
        this._txtBox.setValue(name || "");
    }

    public getValue()
    {
        return this._value;
    }

    private _closeSelection()
    {
        $$.hide(this._pnlMatches);
        this._activeItem = null;
        window.removeEventListener("resize", this._windowResizeHandler as any);
        this._showingOptions = false;

        if (this._currentAbortController)
        {
            this._currentAbortController.abort();
            this._currentAbortController = null;
        }

        if (this._timeoutSearch)
        {
            window.clearTimeout(this._timeoutSearch);
            this._timeoutSearch = null;
        }
    }

    private _loadFailure()
    {
        this._currentAbortController = null;
    }

    private _position()
    {
        var inputBounds = $$.getBounds(this._txtBox.Container);

        this._pnlMatches.style.width = "";
        this._pnlMatches.style.height = "";
        var pnlBounds = $$.getBounds(this._pnlMatches);
        this._pnlMatches.style.width = (inputBounds.width > pnlBounds.width ? inputBounds.width : pnlBounds.width) + "px";

        let viewTop = this._txtBox.Container.getBoundingClientRect().top;
        let top = viewTop + window.scrollY;
        this._pnlMatches.style.top = inputBounds.height + top + "px";
        this._pnlMatches.style.left = inputBounds.x + "px";

        $$.removeCssClass(this._pnlMatches, "bottom");

        if (viewTop + 150 > $(window).height())
        {
            $$.addCssClass(this._pnlMatches, "top");
            this._pnlMatches.style.top = "auto";
            this._pnlMatches.style.bottom = inputBounds.height + "px";
        }
        else
        {
            this._pnlMatches.style.top = inputBounds.height + top + "px";
            if ($(this._pnlMatches).height() + viewTop + inputBounds.height > $(window).height())
            {
                this._pnlMatches.style.height = ($(window).height() - (viewTop + inputBounds.height + 10)) + "px";
            }
            this._pnlMatches.style.bottom = "auto";
        }
    }

    private _handlePopupCancel()
    {
        this._popup.close();
    }

    public editRecord(data)
    {
        this._editControl.setData(data);
        this._popup.open();
    }

    private _renderItem(item, index)
    {
        return (
            <li key={item.Key}
                data-index={index}>
                <span style={{ display: "inline-block", verticalAlign: "top", paddingLeft: "5px", width: "225px", wordBreak: "break-word", whiteSpace: "normal" }}>
                    {item.Value}
                    {item.SubContext ? <span><br /> <span className="subcontext" title={item.SubContext}>{item.SubContext}</span></span> : <span />}
                    {item.Context ? <span><br /> <span className="context">{item.Context}</span></span> : <span />}
                </span>
                {item.ImagePath ? <img style={{ display: "inline-block", width: "65px" }} loading="lazy" src={item.ImagePath} /> : null}
            </li>
        );
    }

    render(): JSX.Element
    {
        if (!this.props.style)
        {
            this.props.style = {};
        }
        if (!this.props.style.width)
        {
            this.props.style.width = "180px";
        }

        return (
            <span ref={(container) => this._registerContainer(container)} style={{ position: "relative" }}>
                <TextBox Descriptor={this.props.Descriptor} ref={(txtBox) => { this._txtBox = txtBox; }}
                    onFocus={this._onFocus.bind(this)}
                    onBlur={this._onBlur.bind(this)}
                    onKeyDown={this._handleTxtKeyDown.bind(this)}
                    onKeyPress={this._handleTxtKeyPress.bind(this)}
                    style={this.props.style}
                    Watermark={this.props.Watermark}
                />
                <RenderInBody>
                    <div ref={(pnlMatches) => { this._pnlMatches = pnlMatches; }} className="autopanel" style={{ display: "none" }}>
                        <ul className="autolist"
                            onMouseDown={this._handleLstMouseDown.bind(this)}
                            ref={(autoList) => { this._autoList = autoList; }}>
                            <li ref={(ref) => this._noMatchOption = ref} style={{ pointerEvents: "none" }}>No matches found</li>
                            {
                                this.props.CreateComponent && (<li ref={(ref) => this._addNewOption = ref}><img src="../../Images/add.png" />Add New</li>)
                            }
                        </ul>
                    </div>
                </RenderInBody>
                {this.props.CreateComponent ? (<Popup ref={(ref) => this._popup = ref} onClose={this._handlePopupCancel.bind(this)}>{preact.createElement<{ onSave?, onCancel?, ref?}>(this.props.CreateComponent, { onSave: this._handleNew.bind(this), onCancel: this._handlePopupCancel.bind(this), ref: (ref) => { this._editControl = ref as DataPanel<unknown> } })}</Popup>) : null}
            </span>
        );
    }
}